import { useEffect, useState } from 'react';
import { useNavigate, generatePath, useParams } from 'react-router-dom';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { isSuccessful } from 'pages/rates/components/RatesForm/ratesForm.utils';
import { SubmitHandler } from 'react-hook-form';
import { RatesTab, reactPaths } from 'root/pathnames';
import { RatesType } from 'root/pathnames/tabs';
import { appToast } from 'utils/toast.utils';
import { Rate } from '../SideTable/types';
import {
  DUPLICATE_ERROR_TOAST,
  DUPLICATE_LABEL,
  PUBLICATE_FAILURE_TOAST,
  PUBLICATE_SUCCESS_TOAST,
  DEPUBLISH_FAILURE_TOAST,
  DEPUBLISH_SUCCESS_TOAST,
  PUBLISH_LABEL,
  DEPUBLISH_LABEL,
} from './ActionButtons.consts';
import * as S from './ActionButtons.css';
import { useDraftStormRate, usePublishStormRate } from 'api/rates';

type Props = {
  rateData: Rate;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setValidateAll: React.Dispatch<React.SetStateAction<boolean>>;
  refetchRateData: (rateId: string) => void;
};

export const ActionButtons = ({
  rateData,
  isEditing,
  setIsEditing,
  setValidateAll,
  refetchRateData,
}: Props) => {
  const navigate = useNavigate();
  const { tab: ratesTab } = useParams<{ tab: RatesTab }>();
  const [publishDisabled, setPublishDisabled] = useState(true);
  const [depublishDisabled, setDepublishDisabled] = useState(true);
  const [duplicateDisabled, setDuplicateDisabled] = useState(true);

  const { loading: publishLoading, publishStormRate } = usePublishStormRate();
  const { loading: draftLoading, draftStormRate } = useDraftStormRate();

  const handleDuplicateRate: SubmitHandler<any> = async (formData) => {
    if (formData) {
      const rateId = formData.id;
      try {
        navigate(
          generatePath(reactPaths.ratesForm, {
            ratesType: RatesType.RATES,
            tab: ratesTab,
          }),
          {
            state: {
              rateId: rateId,
            },
          },
        );
      } catch (error) {
        appToast.error(DUPLICATE_ERROR_TOAST);
      }
    }
  };

  const handleDataPublication = async (rateData: Rate) => {
    const rateId = rateData.id;

    if (rateData && rateId) {
      try {
        const response = await publishStormRate(String(rateId));
        if (response?.status === 200 && isSuccessful(response)) {
          appToast.success(PUBLICATE_SUCCESS_TOAST);
          refetchRateData(String(rateId));
        } else {
          appToast.error(PUBLICATE_FAILURE_TOAST);
          setValidateAll(true);
          setIsEditing(true);
        }
      } catch (error) {
        appToast.error(PUBLICATE_FAILURE_TOAST);
      }
    }
  };

  const handleDataDepublication = async (rateData: Rate) => {
    const rateId = rateData.id;

    if (rateData && rateId) {
      try {
        const response = await draftStormRate(String(rateId));
        if (response?.status === 200 && isSuccessful(response)) {
          appToast.success(DEPUBLISH_SUCCESS_TOAST);
          refetchRateData(String(rateId));
        } else {
          appToast.error(DEPUBLISH_FAILURE_TOAST);
          setValidateAll(true);
          setIsEditing(true);
        }
      } catch (error) {
        appToast.error(DEPUBLISH_FAILURE_TOAST);
      }
    }
  };

  useEffect(() => {
    setPublishDisabled(!!rateData.publicationDate || isEditing);
    setDepublishDisabled(!rateData.publicationDate || isEditing);
    setDuplicateDisabled(isEditing);
  }, [rateData, isEditing]);

  return (
    <>
      <LoaderFullScreen loading={publishLoading || draftLoading} />
      <S.ActionButtonsContainer>
        <StyledButton
          onClick={() => handleDuplicateRate(rateData)}
          disabled={duplicateDisabled || publishLoading || draftLoading}
          $primary
        >
          {DUPLICATE_LABEL}
        </StyledButton>
        <StyledButton
          disabled={publishDisabled || publishLoading || draftLoading}
          onClick={() => handleDataPublication(rateData)}
          $primary
        >
          {PUBLISH_LABEL}
        </StyledButton>
        <StyledButton
          disabled={depublishDisabled || draftLoading || publishLoading}
          onClick={() => handleDataDepublication(rateData)}
          $primary
        >
          {DEPUBLISH_LABEL}
        </StyledButton>
      </S.ActionButtonsContainer>
    </>
  );
};
